import { useMemo, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, Info } from 'lucide-react'
import {
  COMPOSED_CONTEXT_BUDGET_CHARS,
  summarizeContextPrecedence,
  type ContextSliceSummary,
  type DocumentDetail,
  type WorkspaceContextRead,
} from '@/api/docfiller-types'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'

/**
 * Show the user, BEFORE a Fill / generation run, exactly which saved-context
 * slices will compose the agent's "saved context" section + how much of the
 * composed-prompt char budget they will consume.
 *
 * Mirrors ``compose_fill_context`` in the backend so the display matches
 * what the prompt will actually contain. The composed budget cap is
 * communicated visibly so the user can prune a slice rather than discovering
 * the backend truncated it after a fill.
 */
export interface ContextPrecedencePanelProps {
  doc: DocumentDetail
  documentScope: WorkspaceContextRead | null | undefined
  userScope: WorkspaceContextRead | null | undefined
  /** When true (default), starts expanded. Compact (false) renders only the summary line. */
  defaultExpanded?: boolean
  className?: string
  /** Force readOnly UX for users without write access (renders sliced as read-only chips). */
  readOnly?: boolean
}

export function ContextPrecedencePanel({
  doc,
  documentScope,
  userScope,
  defaultExpanded = true,
  className,
  readOnly = false,
}: ContextPrecedencePanelProps) {
  const summary = useMemo(
    () =>
      summarizeContextPrecedence({
        documentSnapshot: doc.initial_context ?? null,
        documentScope: documentScope?.content ?? null,
        userScope: userScope?.content ?? null,
      }),
    [doc.initial_context, documentScope?.content, userScope?.content],
  )
  const [expanded, setExpanded] = useState(defaultExpanded)

  const percent = Math.min(
    100,
    Math.round((summary.composedChars / summary.composedBudgetChars) * 100),
  )
  const tone = summary.composedOverBudget
    ? 'danger'
    : percent >= 80
      ? 'warning'
      : 'primary'

  const activeCount = summary.activeSlices.length
  const summaryLabel =
    activeCount === 0
      ? 'No saved context attached — the agent will rely on the source document + your chat instructions only.'
      : `Composing ${activeCount} of ${summary.slices.length} saved context slice${
          activeCount === 1 ? '' : 's'
        } into the agent prompt.`

  return (
    <section
      data-testid="context-precedence-panel"
      data-readonly={readOnly || undefined}
      aria-labelledby="context-precedence-heading"
      className={cn(
        'space-y-3 rounded-lg border border-border bg-card/60 p-3',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="context-precedence-detail"
        className="flex w-full items-center gap-2 text-left"
        data-testid="context-precedence-toggle"
      >
        {expanded ? (
          <ChevronDown className="size-3.5 text-muted-foreground" aria-hidden />
        ) : (
          <ChevronRight className="size-3.5 text-muted-foreground" aria-hidden />
        )}
        <span
          id="context-precedence-heading"
          className="aurora-eyebrow text-foreground"
        >
          Context precedence
        </span>
        {readOnly && (
          <Badge tone="neutral" size="xs" data-testid="context-precedence-readonly">
            Read-only
          </Badge>
        )}
        <Badge
          tone={tone}
          size="xs"
          data-testid="context-precedence-count"
          className="ml-auto"
        >
          {activeCount} active
        </Badge>
      </button>

      <p
        className="text-xs leading-relaxed text-muted-foreground"
        data-testid="context-precedence-summary"
      >
        {summaryLabel}
      </p>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span>Composed prompt budget</span>
          <span
            className={cn(
              'font-mono tabular-nums',
              summary.composedOverBudget && 'text-destructive',
            )}
            data-testid="context-precedence-budget-label"
          >
            {summary.composedChars.toLocaleString()} /{' '}
            {summary.composedBudgetChars.toLocaleString()} chars
          </span>
        </div>
        <Progress
          percent={percent}
          size="sm"
          tone={tone}
          data-testid="context-precedence-budget-bar"
        />
        {summary.composedOverBudget && (
          <p
            className="flex items-start gap-1.5 text-[11px] leading-relaxed text-destructive"
            data-testid="context-precedence-over-budget"
            role="alert"
          >
            <AlertTriangle aria-hidden className="mt-0.5 size-3 shrink-0" />
            Over budget — the backend will truncate the lowest-precedence slice
            ({summary.composedChars - COMPOSED_CONTEXT_BUDGET_CHARS} chars over).
          </p>
        )}
      </div>

      {expanded && (
        <ol
          id="context-precedence-detail"
          className="space-y-1.5"
          data-testid="context-precedence-list"
        >
          {summary.slices.map((slice, idx) => (
            <PrecedenceRow
              key={slice.key}
              slice={slice}
              order={idx + 1}
              userScopeShadowed={
                slice.key === 'user' && summary.userScopeShadowed
              }
            />
          ))}
        </ol>
      )}
    </section>
  )
}

interface PrecedenceRowProps {
  slice: ContextSliceSummary
  order: number
  userScopeShadowed: boolean
}

function PrecedenceRow({ slice, order, userScopeShadowed }: PrecedenceRowProps) {
  const inactiveReason = !slice.active
    ? userScopeShadowed
      ? 'Shadowed by the document snapshot — backend only reads this slice when the snapshot is empty.'
      : 'Empty — nothing to add to the prompt.'
    : null

  return (
    <li
      data-testid={`context-precedence-row-${slice.key}`}
      data-active={slice.active}
      data-shadowed={userScopeShadowed || undefined}
      className={cn(
        'flex items-start gap-2 rounded-md border px-2.5 py-2 text-xs leading-relaxed',
        slice.active
          ? 'border-primary/40 bg-primary-soft/40 text-foreground'
          : 'border-border bg-card text-muted-foreground',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
          slice.active
            ? 'bg-primary text-primary-foreground'
            : 'bg-muted text-muted-foreground',
        )}
      >
        {order}
      </span>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-foreground">{slice.label}</span>
          {slice.active ? (
            <Badge tone="primary" size="xs" data-testid={`slice-active-${slice.key}`}>
              Will apply
            </Badge>
          ) : (
            <Badge tone="neutral" size="xs" data-testid={`slice-inactive-${slice.key}`}>
              {userScopeShadowed ? 'Shadowed' : 'Empty'}
            </Badge>
          )}
          {slice.active && (
            <span
              className="ml-auto text-[10px] font-mono tabular-nums text-muted-foreground"
              data-testid={`slice-chars-${slice.key}`}
            >
              {slice.chars.toLocaleString()} chars
            </span>
          )}
        </div>
        <p>{slice.description}</p>
        {inactiveReason && (
          <p
            className="flex items-start gap-1 text-[10px] leading-relaxed text-muted-foreground/90"
            data-testid={`slice-inactive-reason-${slice.key}`}
          >
            <Info aria-hidden className="mt-0.5 size-3 shrink-0" />
            {inactiveReason}
          </p>
        )}
      </div>
    </li>
  )
}
